import { useState } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';

const JobCard = ({ job, onViewDetails, onQuickApply, viewMode = 'card' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent':
        return 'bg-[#FF6A00] text-white border-[#FF6A00]';
      case 'high':
        return 'bg-warning/10 text-warning border-warning/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Plumbing': 'Wrench',
      'Electrical': 'Zap',
      'Carpentry': 'Hammer',
      'Cleaning': 'Sparkles',
      'Babysitting': 'Baby',
      'Tutoring': 'GraduationCap',
      'Driving': 'Car',
      'Cooking': 'ChefHat',
      'Laundry': 'Shirt',
      'Errands': 'ShoppingBag',
      'Tech Support': 'Laptop',
      'Beauty Services': 'Scissors'
    };
    return icons?.[category] || 'Briefcase';
  };

  const formatDate = (date) => {
    const now = new Date();
    const jobDate = new Date(date);
    const diffTime = Math.abs(now - jobDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return jobDate?.toLocaleDateString('en-PH', { day: '2d', month: '2d', year: 'numeric' });
  };

  if (viewMode === 'list') {
    return (
      <div 
        className="bg-white border border-gray-100 rounded-xl p-4 md:p-6 hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-smooth cursor-pointer"
        onClick={() => onViewDetails(job)}
      >
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-[#1E73BE]/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon name={getCategoryIcon(job?.category)} size={24} color="#1E73BE" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground line-clamp-1">
                  {job?.title}
                </h3>
                {job?.isVerified && (
                  <div className="flex-shrink-0 w-5 h-5 bg-[#5CD600]/20 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={12} color="#5CD600" />
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs md:text-sm px-2 py-1 bg-[#1E73BE]/10 text-[#1E73BE] rounded-md font-medium">
                  {job?.category}
                </span>
                {job?.urgency !== 'normal' && (
                  <span className={`text-xs md:text-sm px-2 py-1 rounded-md font-medium border ${getUrgencyColor(job?.urgency)}`}>
                    {job?.urgency === 'urgent' ? 'Urgent' : 'High Priority'}
                  </span>
                )}
                <span className="text-xs md:text-sm text-muted-foreground flex items-center gap-1">
                  <Icon name="Users" size={14} />
                  {job?.applicantCount} applied
                </span>
              </div>
              
              <p className="text-sm md:text-base text-muted-foreground line-clamp-2 mb-3">
                {job?.description}
              </p>
              
              <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name="MapPin" size={14} color="#1E73BE" />
                  {job?.location}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Calendar" size={14} />
                  {formatDate(job?.postedDate)}
                </span>
                <span className="flex items-center gap-1 font-semibold text-[#FF6A00]">
                  <Icon name="Wallet" size={14} color="#FF6A00" />
                  ₱{job?.budgetMin?.toLocaleString()} - ₱{job?.budgetMax?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex lg:flex-col gap-2 lg:w-32 flex-shrink-0">
            <Button
              variant="default"
              size="sm"
              fullWidth
              onClick={(e) => {
                e?.stopPropagation();
                onQuickApply(job);
              }}
              iconName="Send"
              iconPosition="left"
              className="bg-[#1E73BE] hover:bg-[#2A85D8]"
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-white border border-gray-100 rounded-lg hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-smooth cursor-pointer overflow-hidden"
      onClick={() => onViewDetails(job)}
    >
      <div className="p-3">
        <div className="flex items-start gap-2 mb-2">
          <div className="w-10 h-10 bg-[#1E73BE]/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name={getCategoryIcon(job?.category)} size={18} color="#1E73BE" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-heading font-semibold text-foreground line-clamp-1 mb-1">
              {job?.title}
            </h3>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs px-1.5 py-0.5 bg-[#1E73BE]/10 text-[#1E73BE] rounded">
                {job?.category}
              </span>
              {job?.urgency !== 'normal' && (
                <span className={`text-xs px-1.5 py-0.5 rounded border ${getUrgencyColor(job?.urgency)}`}>
                  {job?.urgency === 'urgent' ? 'Urgent' : 'High'}
                </span>
              )}
            </div>
          </div>
          {job?.isVerified && (
            <div className="w-4 h-4 bg-[#5CD600]/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="Check" size={10} color="#5CD600" />
            </div>
          )}
        </div>
        
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2 leading-relaxed">
          {job?.description}
        </p>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span className="flex items-center gap-1">
            <Icon name="MapPin" size={12} color="#1E73BE" />
            <span className="truncate">{job?.location}</span>
          </span>
          <span className="flex items-center gap-1">
            <Icon name="Users" size={12} />
            {job?.applicantCount}
          </span>
        </div>
        
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div>
            <span className="text-xs text-muted-foreground block">Budget</span>
            <span className="font-semibold text-[#FF6A00] text-xs">
              ₱{job?.budgetMin?.toLocaleString()}-{job?.budgetMax?.toLocaleString()}
            </span>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={(e) => {
              e?.stopPropagation();
              onQuickApply?.(job);
            }}
            className="bg-[#1E73BE] hover:bg-[#2A85D8] text-xs px-3 py-1.5 h-auto"
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JobCard;